import { api } from '../../lib/api.ts';
import type { AddressSnapshot } from '../address-book/AddressPicker.tsx';

export type LetterStatus = 'DRAFT' | 'FINALIZED';

// Mirrors the backend Letter model (prisma/schema.prisma) + /api/letters
// response shape. letterDate/finalizedAt/createdAt/updatedAt travel as ISO
// strings over JSON.
export interface Letter {
  id: string;
  orgId: string;
  authorId: string;
  status: LetterStatus;
  subject: string;
  letterDate: string;
  letterNumber: string | null;
  addressTo: AddressSnapshot;
  addressBookEntryId: string | null;
  referringLetter: string | null;
  bodyHtml: string;
  targetPages: number;
  signatoryName: string;
  signatoryDesignation: string | null;
  pdfPath: string | null;
  pdfPageCount: number | null;
  pdfFitScale: number | null;
  pdfFitOk: boolean | null;
  finalizedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLetterInput {
  subject: string;
  letterDate: string;
  addressTo: AddressSnapshot;
  addressBookEntryId?: string;
  referringLetter?: string;
  targetPages?: number;
  signatoryName: string;
  signatoryDesignation?: string;
  letterNumber?: string; // manual, user-typed — never auto-generated
}

// Everything but bodyHtml is also settable through the field-column draft
// autosave; bodyHtml is included for Task 10's editor to reuse the same PATCH.
export type UpdateLetterInput = Partial<CreateLetterInput> & {
  bodyHtml?: string;
};

export const lettersApi = {
  list: () => api.get<{ letters: Letter[] }>('/letters').then((r) => r.data.letters),
  get: (id: string) => api.get<{ letter: Letter }>(`/letters/${id}`).then((r) => r.data.letter),
  create: (input: CreateLetterInput) =>
    api.post<{ letter: Letter }>('/letters', input).then((r) => r.data.letter),
  update: (id: string, patch: UpdateLetterInput) =>
    api.patch<{ letter: Letter }>(`/letters/${id}`, patch).then((r) => r.data.letter),
  preview: (id: string) => api.post<{ ok: true }>(`/letters/${id}/preview`).then((r) => r.data),
  finalize: (id: string) => api.post<{ letter: Letter }>(`/letters/${id}/finalize`).then((r) => r.data.letter),
  reopen: (id: string) => api.post<{ letter: Letter }>(`/letters/${id}/reopen`).then((r) => r.data.letter),
  remove: (id: string) => api.delete(`/letters/${id}`),
  // The PDF endpoint requires the Bearer token (see backend/src/routes/letters.ts) —
  // a plain <a href>/window.open to it 401s because the browser doesn't attach the
  // JS-held access token. Fetch it through the authed axios instance as a blob
  // instead; callers turn that into an object URL (see LetterEditor.tsx).
  pdfBlob: (id: string) => api.get(`/letters/${id}/pdf`, { responseType: 'blob' }).then((r) => r.data as Blob),
};
